import React from 'react'
import PropTypes from 'prop-types'
import UtmUtils from '../../utils/utmUtils'
import styled, { css, keyframes } from 'styled-components'
import { useProducts, usePDP, FeatureProduct } from 'ui'
import { useSelector } from 'react-redux'
import { useViews } from 'state'
import { StoreInfoFloating } from '../storeInfoFloating'
import { CallBottomContainer } from './BottomContainer'
import { selectCurrentStore } from '../../reducers/storeSlice'
import { ExternalReactions } from '../Call/ExternalReactions'
import { BaseCallLayout, MainContent, Row } from './Layout.styles'
import { TopNavBar } from './TopNavBar'
import { PopUp } from '../GJKit/PopUp'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { CartCount } from 'ui'
import qs from 'qs'
import { RoundedButton } from '../Kit/Buttons'
import { Cameras } from '../GJKit/Cameras/Cameras'
import { useDevices } from '@gojiraf/responsive'
import { useCart } from 'ui'
import { Carousel, CarouselContent, CarouselItem } from 'ui'
import { useUtm } from '@gojiraf/useutm'
import QuickAddButton from './QuickAddButton'
import { StartEventCountdown } from './StartEventCountdown'
import { FinishEventCountdown } from './FinishEventCountdown'
import { useCheckout } from '../../hooks/useCheckout'

const CallLayout = ({ children, popUpData }) => {
  const { t } = useTranslation()
  const { isDesktop } = useDevices()
  const [searchParams] = useSearchParams()
  const isIframe = searchParams.get('iframe') === 'true'
  const { postHogTrackEvent } = usePostHogAnalytics()
  const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true })
  const { utm_medium } = UtmUtils.getUtmObject(queryParams)
  const { showUIComponents } = useUtm(utm_medium)
  const { featuredProduct, animateFeaturedProduct, products } = useProducts()
  const store = useSelector(selectCurrentStore)
  const { isOneToManySale, storeConfigurations } = store
  const { state, send } = useViews()
  const { send: sendPDP } = usePDP()
  const { isLightIntegration, isVtexExternalCart } = useCheckout(store)
  //Verificar como se comporta un producto que tenga un solo tipo de variante y varias variantes
  const featuredProductHasVariants = featuredProduct?.skus.length > 1
  const { state: cartState, send: cartSend } = useCart()
  const renderCameras = !isOneToManySale && state.matches('secondary.showingParticipants') === true

  const noQuantityLimitReached = (item) => {
    const quantityInCart = cartState.context.variants.get(item.id) ?? 0
    return item.limitPerOrder === undefined || quantityInCart < item.limitPerOrder
  }

  const handleProductClicked = (product) => {
    if (
      product?.skus.length > 1 ||
      isLightIntegration ||
      isVtexExternalCart ||
      product.price === undefined
    ) {
      postHogTrackEvent(
        product.main === true ? `product-featured` : `product-click-detail-button`,
        {
          from: product.main === true ? 'featured' : 'product',
          productName: product.name,
        },
      )
      sendPDP({
        type: 'SET_PRODUCT',
        productId: product.id,
      })
      send({
        type: 'SHOW_PDP',
      })
    } else {
      postHogTrackEvent('cart-initialized', {
        from: 'product',
      })
      if (noQuantityLimitReached(product.skus[0])) {
        cartSend({
          type: 'Add product variant',
          productVariantId: product.skus[0].id,
          quantity: 1,
        })
      }
      if (!isVtexExternalCart) {
        send({
          type: 'SHOW_CART',
        })
      }
    }
  }

  return (
    <BaseCallLayout
      isDesktop={isDesktop}
      camerasAndProducts={renderCameras || !!featuredProduct}
      data-test="landing-OTM"
    >
      <ExternalReactions />
      {showUIComponents && (
        <Row>
          <StoreInfoFloating />
          {!isDesktop ? <TopNavBar isIframe={isIframe} showPIPButton /> : null}
        </Row>
      )}
      <Row>
        <PopUp data={popUpData} />

        {!isDesktop && showUIComponents && (
          <CartAndMediaControlsContainer>
            {!isLightIntegration && !isVtexExternalCart && !isDesktop ? (
              state.matches('secondary.showingCart') === true ? (
                <RoundedButton filled layout>
                  <CartCount className="text-white bg-white fill-black" />
                </RoundedButton>
              ) : (
                <RoundedButton filled layout>
                  <CartCount className="text-black" />
                </RoundedButton>
              )
            ) : null}
          </CartAndMediaControlsContainer>
        )}
      </Row>

      {showUIComponents && featuredProduct && (
        <QuickAddButton featuredProduct={featuredProduct} store={store} />
      )}

      <StartEventCountdown />
      <FinishEventCountdown />

      <MainContent>{children}</MainContent>

      {featuredProduct && isDesktop && showUIComponents && (
        <ProductChangedAnimation featuredProductChanged={animateFeaturedProduct}>
          <FeatureProduct
            propText={
              !featuredProductHasVariants &&
              !isLightIntegration &&
              !isVtexExternalCart &&
              featuredProduct.price !== undefined
                ? t('products.addToCart')
                : ''
            }
            product={featuredProduct}
            onProductClicked={handleProductClicked}
          />
        </ProductChangedAnimation>
      )}

      {showUIComponents && (
        <CallBottomContainer isDesktop={isDesktop} data-test="mobile-action-bar-container" />
      )}

      {!isDesktop && showUIComponents && (
        <CamerasAndFeaturedProductContainer>
          {renderCameras ? (
            <Cameras allowsMultipleBuyers={storeConfigurations.allowsMultipleBuyers} />
          ) : featuredProduct ? (
            <>
              {products?.length > 1 ? (
                <Carousel
                  orientation="horizontal"
                  className="size-full"
                  featuredProductId={featuredProduct.id}
                >
                  <CarouselContent className="flex">
                    {products.map((product, index) => (
                      <CarouselItem
                        key={index}
                        featuredProductId={featuredProduct.id}
                        productsList={products}
                      >
                        <FeatureProduct
                          propText={
                            !product?.variants?.length > 0 &&
                            !isLightIntegration &&
                            !isVtexExternalCart &&
                            product.price !== undefined
                              ? t('products.addToCart')
                              : ''
                          }
                          product={product}
                          onProductClicked={handleProductClicked}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              ) : (
                <ProductChangedAnimation featuredProductChanged={animateFeaturedProduct}>
                  <FeatureProduct
                    propText={
                      !featuredProductHasVariants &&
                      !isLightIntegration &&
                      featuredProduct.price !== undefined
                        ? t('products.addToCart')
                        : ''
                    }
                    product={featuredProduct}
                    onProductClicked={handleProductClicked}
                  />
                </ProductChangedAnimation>
              )}
            </>
          ) : null}
        </CamerasAndFeaturedProductContainer>
      )}
    </BaseCallLayout>
  )
}

CallLayout.propTypes = {
  children: PropTypes.element,
  className: PropTypes.string,
  loading: PropTypes.bool,
  popUpData: PropTypes.object,
}

const scaleInCenter = keyframes`
    0% {
    -webkit-transform: scale(0);
            transform: scale(0);
    opacity: 1;
  }
  100% {
    -webkit-transform: scale(1);
            transform: scale(1);
    opacity: 1;
  }
`

const ProductChangedAnimation = styled.div`
  z-index: 1;
  padding: 0 1rem 1rem 1rem;
  animation: ${({ featuredProductChanged }) =>
    featuredProductChanged
      ? css`
          ${scaleInCenter} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both
        `
      : 'none'};
  width: 100%;
  border-radius: 0.5rem;
`

const CartAndMediaControlsContainer = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`
const CamerasAndFeaturedProductContainer = styled.div`
  z-index: 1;
`

export { CallLayout }
